// Utils
import { isWebBridgeSupported } from '@shopee-user/platform';
import { postEventValidation } from './validate';
import batchFunction from './batchFunction';
import { isFeatureEnabled as isStaticFeatureEnabled } from '@shopee-user/static-feature-toggle';
import { getNativeVersion } from '@shopee-user/business-customization-logic';

// Constants
import { TrackingSource } from '../constants';
import { config } from '@shopee-user/platform';
import { Feature as StaticFeature } from '@shopee-user/static-feature-toggle';
import { Env, Locale } from '@shopee-user/global';

// Types
import {
  Tracker,
  TrackingEvent,
  TrackingInfo,
  ImpressionTrackingData,
  ImpressionTrackingInfo,
  InitBITrackerParams,
  TrackingV4SdkEvent,
} from '../types';

// V4 SDK
import '@shopee/tracking-bootstrap';

type WebTrackerParams = {
  cookies: Record<string, string>;
  userAgent: string;
  validationUrl: string;
  validationApiToken: string;
};

type Params = {
  appID: number;
  webConfig?: WebTrackerParams;
  enableWebTracking?: boolean;
  enableAppTracking?: boolean;
  enableSchemaValidation?: boolean;
  enableDebugTraceLogs?: boolean;
};

const WEB_TRACKING_CONFIG: {
  PLATFORM_IMPLEMENTATION: string;
  PLATFORM: 'pc_web';
  ENVIRONMENT: Record<string, string>;
  TRACKER_TYPES: Record<string, ('ubt' | 'algo' | 'mmp' | 'debugview')[]>;
  SDK_VERSION: string;
} = {
  /**
   * Please refer to this FE TD on the initialization config:
   * https://confluence.shopee.io/display/SAUT/%5BToB%5D%5BWeb%5D%5BTD%5D+Upgrade+FE+Data+Tracker+to+V4
   */
  PLATFORM_IMPLEMENTATION: 'pc',
  PLATFORM: 'pc_web',
  ENVIRONMENT: {
    TEST: 'test',
    PRODUCTION: 'live',
  },
  TRACKER_TYPES: {
    TEST: ['ubt', 'debugview'],
    PRODUCTION: ['ubt'],
  },
  // TODO: jay.liangjh@shopee.com, gabriella.benedicta@shopee.com
  // we will need to update to 1.1.22 on 2025 Q2 or Q3
  // now 1.1.14 is calling old TMS service domain, which will be deprecated on 2025 Q2 or Q3
  SDK_VERSION: '1.1.14',
};

/**
 * Please refer to this FE TD on TMS Debug View integration
 * https://confluence.shopee.io/x/jHDmw
 */
const WEB_TRACKING_DEBUG_VIEW_CONFIG: {
  getDeviceId: () => string;
  DEBUG_MODE: Record<string, boolean>;
  DEBUG_ENV: 'live' | 'test';
} = {
  getDeviceId: () =>
    localStorage.getItem('de_device_id') || Math.random().toString(36).slice(2),
  DEBUG_MODE: {
    TEST: true,
    PRODUCTION: false,
  },
  DEBUG_ENV: 'live',
};

const { ENV, LOCALE } = config;
const IDLE_TIMEOUT = 1000; // 1s
const BATCH_TIME = 100;
const DEFAULT_WEB_CONFIG: WebTrackerParams = {
  cookies: {},
  userAgent: '',
  validationUrl: '',
  validationApiToken: '',
};

const isTrackingSdkV4Enabled = isStaticFeatureEnabled(
  StaticFeature.TobWebTrackingSdkV4Integration,
  false
);

function createTrackingEvent<T>(info: TrackingInfo<T>): TrackingEvent<T> {
  return {
    type: 'v3',
    timestamp: Date.now(),
    info,
  };
}

function createV4SdkTrackingEventFromV3Event<T>({
  v3TrackingEvent,
}: {
  v3TrackingEvent: TrackingEvent<T>;
}): TrackingV4SdkEvent {
  const { info } = v3TrackingEvent;
  return {
    operation: info.operation,
    target_type: info.target_type || '',
    platform_implementation: WEB_TRACKING_CONFIG.PLATFORM_IMPLEMENTATION,
    data: info.data || {},
    page_section: info.page_section || '',
    page_type: info.page_type || '',
  };
}

export default function createBITracker({
  appID,
  webConfig = DEFAULT_WEB_CONFIG,
  enableWebTracking = false,
  enableAppTracking = false,
  enableSchemaValidation = false,
  enableDebugTraceLogs = false, // enable this to show
}: Params): {
  initTracker: (params: InitBITrackerParams) => Promise<Tracker | undefined>;
  sendTrackingEvent: (event: TrackingInfo<unknown>) => void;
  sendImpressionEvent: (event: ImpressionTrackingInfo<unknown>) => void;
} {
  const { cookies, userAgent, validationUrl, validationApiToken } = webConfig;
  let tracker: Tracker | undefined = undefined;
  let eventBuffer: Array<TrackingEvent<unknown>> = [];

  function logTracingInfo(...logs: unknown[]) {
    if (enableDebugTraceLogs) {
      console.log(...logs);
    }
  }
  function logTracingWarning(...logs: unknown[]) {
    if (enableDebugTraceLogs) {
      console.warn(...logs);
    }
  }

  /**
   * NOTE: Current implementations for tracking only cater for in-app tracking for shopee partner and food driver.
   * Webtracking is NOT tested or supported yet.
   */
  const initWebTracker = async ({
    source,
  }: {
    source: TrackingSource;
  }): Promise<Tracker | undefined> => {
    // no-op if turned off
    if (!enableWebTracking) {
      logTracingWarning(
        'Tracker is not created as web tracking is turned off. Please turn on Feature.WebTracking to enable tracking on web.'
      );
      return {
        sync: ({ data }: { data: TrackingEvent<unknown> }) => {
          logTracingWarning('Skip tracking web event', JSON.stringify(data));
        },
      };
    }

    if (isTrackingSdkV4Enabled) {
      if (!window.trackingSDK) {
        console.error('Error: trackingSDK not available');
        return;
      }
      const isLive = config.ENV === Env.Live || config.ENV === Env.Liveish;
      window.trackingSDK.init(
        isLive
          ? WEB_TRACKING_CONFIG.TRACKER_TYPES.PRODUCTION
          : WEB_TRACKING_CONFIG.TRACKER_TYPES.TEST,
        (name: typeof WEB_TRACKING_CONFIG.TRACKER_TYPES[string][number]) => {
          return {
            platform: WEB_TRACKING_CONFIG.PLATFORM,
            version: getNativeVersion(),
            env: isLive
              ? WEB_TRACKING_CONFIG.ENVIRONMENT.PRODUCTION
              : WEB_TRACKING_CONFIG.ENVIRONMENT.TEST,
            locale:
              config.LOCALE === Locale.GLOBAL
                ? Locale.SG.toLowerCase()
                : config.LOCALE.toLowerCase(),
            appId: appID,
            ...(name === 'debugview'
              ? {
                  device_id: WEB_TRACKING_DEBUG_VIEW_CONFIG.getDeviceId(),
                  debugMode:
                    config.ENV === Env.Live || config.ENV === Env.Liveish
                      ? WEB_TRACKING_DEBUG_VIEW_CONFIG.DEBUG_MODE.PRODUCTION
                      : WEB_TRACKING_DEBUG_VIEW_CONFIG.DEBUG_MODE.TEST,
                  debugEnv: WEB_TRACKING_DEBUG_VIEW_CONFIG.DEBUG_ENV,
                }
              : {}),
            enableAutoTrack: true,
          };
        },
        WEB_TRACKING_CONFIG.SDK_VERSION
      );
      logTracingInfo('Web Tracker (tracking SDK V4) created.');
      const tracker = window.trackingSDK;

      return {
        ...tracker,
        sync: ({ data }: { data: TrackingEvent<unknown> }) => {
          logTracingInfo(
            `Tracking data sent via web using V4 SDK: ${JSON.stringify(data)}`
          );
          if (tracker && tracker.ubt) {
            tracker.ubt(
              'track',
              createV4SdkTrackingEventFromV3Event({
                v3TrackingEvent: data,
              })
            );
          } else {
            console.error('Error: tracking SDK V4 internal error');
          }
        },
      };
    } else {
      const { createTrackingV3Sdk } = await import(
        /* webpackChunkName: "trackingSDK" */ './trackingSDK'
      );

      const tracker = createTrackingV3Sdk({
        appId: appID,
        source,
        env: ENV,
        locale:
          LOCALE === Locale.GLOBAL
            ? Locale.SG.toLowerCase()
            : LOCALE.toLowerCase(),
        platform: '',
        getCookies: () => cookies,
        ua: userAgent,
        enableAutoTrack: false,
        fetch: self.fetch.bind(self),
      }) as Tracker;

      logTracingInfo('Web tracker created.');

      return {
        ...tracker,
        sync: ({ data }: { data: TrackingEvent<unknown> }) => {
          logTracingInfo(`Tracking data sent via web: ${JSON.stringify(data)}`);
          tracker.sync({ data });
        },
      };
    }
  };

  const initAppTracker = ({
    sendBITrackingViaWebView,
    source,
  }: InitBITrackerParams) => {
    // no-op if turned off
    if (!enableAppTracking) {
      logTracingWarning(
        'Tracker is not created as app tracking is turned off. Please turn on Feature.AppTracking to enable tracking on web.'
      );
      return {
        sync: ({ data }: { data: TrackingEvent<unknown> }) => {
          logTracingWarning('Skip tracking app event', JSON.stringify(data));
        },
      };
    }
    logTracingInfo('Create app tracker.');
    // TODO Remove only when web tracking support has been tested and started supporting.
    logTracingWarning(
      'NOTE: currently we only support App tracking, web tracking is not tested yet.'
    );
    return {
      sync: ({ data }: { data: TrackingEvent<unknown> }) => {
        logTracingInfo(
          `Tracking data sent via bridge: ${JSON.stringify(data)}`
        );
        sendBITrackingViaWebView(JSON.stringify([{ ...data, source }]));
      },
    };
  };

  const initTracker = async (params: InitBITrackerParams) => {
    if (tracker === undefined) {
      tracker = isWebBridgeSupported()
        ? initAppTracker({
            sendBITrackingViaWebView: params.sendBITrackingViaWebView,
            source: TrackingSource.RWeb, // when tracking through web view, the source must be rweb
          })
        : await initWebTracker({ source: params.source });

      // flush all tracking events in the buffer
      for (let i = 0; i < eventBuffer.length; i++) {
        if (tracker === undefined) {
          continue;
        }
        void tracker.sync({ data: eventBuffer[i] });
      }
      eventBuffer = [];
    } else {
      logTracingWarning('BI Tracker is init already');
    }

    return tracker;
  };

  async function validateEvent(event: TrackingEvent, schema?: string) {
    if (enableSchemaValidation && schema) {
      const body = [
        {
          id: schema,
          value: event.info,
        },
      ];
      logTracingInfo(
        `Send validation event ${validationUrl}:`,
        JSON.stringify(body)
      );
      const response = await postEventValidation(
        validationUrl,
        body,
        validationApiToken
      );

      let hasError = false;
      if (response.errorCode !== undefined) {
        console.error(response.errorCode, response.errorMessage);
        hasError = true;
      } else if (!Array.isArray(response.result)) {
        console.error('API Response error: ', JSON.stringify(response.result));
        hasError = true;
      } else {
        response.result.forEach(eventResult => {
          if (!eventResult.result) {
            console.error('Invalid tracking event', eventResult.event);
            hasError = true;
          }
        });
      }

      if (!hasError) {
        logTracingInfo('Validation passed');
      }
    }
  }

  function sendEvent<T>(event: TrackingEvent<T>, schema?: string) {
    void validateEvent(event, schema); // validate event regardless if the tracker is initialized
    if (tracker === undefined) {
      eventBuffer.push(event);
    } else if (isWebBridgeSupported()) {
      // for webview, we send immediately and do not use requestIdleCallback in case the webview is closed immediately upon sending the tracking event.
      // e.g click on back button, click on help button
      void tracker.sync({ data: event });
    } else {
      window.requestIdleCallback(
        () => {
          if (tracker === undefined) {
            return;
          }

          void tracker.sync({ data: event });
        },
        { timeout: IDLE_TIMEOUT }
      );
    }
  }

  function sendTrackingEvent<T>(event: TrackingInfo<T>) {
    const { schema, ...eventData } = event;
    const { page_type } = eventData;

    // Require schema to be defined before it is sent.
    if (schema) {
      if (!page_type) {
        // DEBUG: If page_type is not defined but schema is, probably an error, so show an error in the console.
        // Note that this does not show up on Live and Liveish env as consoles will be stripped up.
        console.error('Page Type not defined! Schema:', schema);
      }
      sendEvent(createTrackingEvent<T>(eventData), schema);
    }
  }

  const sendImpressionInBatch = batchFunction(
    <T>(events: ImpressionTrackingInfo<T>[]) => {
      const eventMap: {
        [key: string]: ImpressionTrackingInfo<ImpressionTrackingData<T>>;
      } = {};
      const schemaMap: {
        [key: string]: string | undefined;
      } = {};

      events.forEach((event: ImpressionTrackingInfo<T>) => {
        const {
          page_type,
          page_section,
          target_type,
          operation,
          data,
          schema,
        } = event;
        const key =
          schema || JSON.stringify({ page_type, page_section, target_type });

        if (!eventMap[key]) {
          eventMap[key] = {
            operation,
            page_type,
            page_section,
            target_type,
            data: { viewed_objects: [] },
          };
          schemaMap[key] = schema;
        }

        const impressionTrackingData = eventMap[key];
        data && impressionTrackingData?.data?.viewed_objects.push({ ...data });
      });

      for (const key in eventMap) {
        sendEvent(createTrackingEvent(eventMap[key]), schemaMap[key]);
      }
    },
    BATCH_TIME
  );

  function sendImpressionEvent<T>(event: ImpressionTrackingInfo<T>) {
    const { schema, page_type } = event;

    // Require schema to be defined before it is sent.
    if (schema) {
      if (!page_type) {
        // DEBUG: If page_type is not defined but schema is, probably an error, so show an error in the console.
        // Note that this does not show up on Live and Liveish env as consoles will be stripped up.
        console.error('Page Type not defined! Schema:', schema);
      }
      sendImpressionInBatch(event);
    }
  }

  return {
    initTracker,
    sendTrackingEvent,
    sendImpressionEvent,
  };
}
